import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { User } from "types";

export type EmploymentHistoryDeleteResponse = {
  success: boolean;
  message: string;
  data: User;
};

const deleteEmploymentHistory = async (
  userId: string,
  entryId: string
): Promise<EmploymentHistoryDeleteResponse> => {
  const { data } = await customAxios.delete<EmploymentHistoryDeleteResponse>(
    `/users/${userId}/employment-history/${entryId}`
  );
  return data;
};

export const useDeleteEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation<
    EmploymentHistoryDeleteResponse,
    AxiosError,
    { userId: string; entryId: string }
  >(
    ({ userId, entryId }) => deleteEmploymentHistory(userId, entryId),
    {
      onSuccess: async (data, variables) => {
        showNotification({
          title: "Success",
          message: data.message ?? "Employment history deleted successfully",
          color: "green",
        });

        await queryClient.invalidateQueries(["users"]);
        await queryClient.invalidateQueries(["user", variables.userId]);
        await queryClient.invalidateQueries([
          "employmentHistory",
          variables.userId,
        ]);
      },
      onError: (error) =>
        showNotification({
          title: "Error",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to delete employment history",
          color: "red",
        }),
    }
  );
};
